import React, { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { Breadcrumbs, Link, Tooltip, LinearProgress, Chip } from "@mui/joy";
import Box from "@mui/joy/Box";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import SmartToyRounded from "@mui/icons-material/SmartToyRounded";
import ConstructionRoundedIcon from "@mui/icons-material/ConstructionRounded";
import Typography from "@mui/joy/Typography";
import Sheet from "@mui/joy/Sheet";
import Button from "@mui/joy/Button";
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import ApiService from "../../../axios/Bookings-Axios";
import GeneralApiService, { GetFranchiseVans, GetUser } from "../../../axios/Axios";
import BookingsDrawer from "../../modals/BookingsDrawer";
import RotaModal from "../../modals/RotaModal";
import RecurringBookingsModal from "../../modals/RecurringBookingsModal";
import ConfirmMoveBookingModal from "../../modals/ConfirmMoveBookingModal";
import './CalendarTheme.css';

// Mobile breakpoint check
const isMobileView = () => typeof window !== 'undefined' && window.innerWidth < 600;

// Interface definitions
interface Van {
    id: number;
    name: string;
    weekly_schedule: Record<string, boolean>;
    blocked_dates: Array<{ start_date: string; end_date: string; reason?: string }>;
    day_overrides?: Array<{ date: string; is_working_override: boolean; reason?: string }>;
}

interface Franchise {
    location: string;
}

interface FranchisePostcode {
    franchise: Franchise;
}

interface Customer {
    full_name: string;
    postcode: string;
    franchise_postcode: FranchisePostcode;
}

interface Service {
    name: string;
}

interface Booking {
    id: number;
    customer: Customer;
    booked_in: string;
    services: Service[];
    recurring_booking?: {
        id: number;
    };
    days_to_complete?: number;
    allocated_dates?: string[];
    allocated_end_date?: string | null;
    van?: Van | null;
    job_status?: string;
    paid?: boolean;
    returns?: BookingReturn[];
    pre_booking_stories?: number;
    ai_confirmed?: boolean | null;
    ai_confirmation_note?: string;
    referral_badge_label?: string;
    is_referral_reward?: boolean;
    is_referred_booking?: boolean;
}

interface BookingReturn {
    id: number;
    return_date: string;
    days_to_complete?: number;
    reason: string;
    van: Van | null;
}

interface PendingDrop {
    info: any;
    bookingId: number;
    newDate: string;
    bookingTitle: string;
}

const formatServices = (services: Service[]): string => {
    const serviceNames = services.map(service => service.name);
    if (serviceNames.length === 0) return "No service selected.";
    if (serviceNames.length === 1) return serviceNames[0];
    if (serviceNames.length === 2) return `${serviceNames[0]} and ${serviceNames[1]}`;

    const lastService = serviceNames.pop();
    return `${serviceNames.join(', ')}, and ${lastService}`;
};

const JS_DAY_TO_NAME = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const toDateKey = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const extractDateKey = (value: string): string => {
    return toDateKey(new Date(value));
};

const isVanWorkingOnDate = (van: Van | null | undefined, date: Date): boolean => {
    if (!van) return true;

    const dateKey = toDateKey(date);
    const override = van.day_overrides?.find((item) => item.date === dateKey);
    if (override) return !!override.is_working_override;

    const inBlockedRange = van.blocked_dates?.some(
        (item) => item.start_date <= dateKey && dateKey <= item.end_date
    );
    if (inBlockedRange) return false;

    const dayName = JS_DAY_TO_NAME[date.getDay()];
    return !!van.weekly_schedule?.[dayName];
};

const cleanOverrideReason = (reason?: string): string => {
    if (!reason || reason === 'Manual off day override') return '';
    return reason;
};

const cleanBlockedReason = (reason?: string): string => {
    if (!reason || reason === 'Unavailable') return '';
    return reason;
};

const buildUnavailableReasonEvents = (van: Van | null | undefined): Array<{
    id: string;
    title: string;
    start: Date;
    end: Date;
    allDay: boolean;
    display: string;
    classNames: string[];
    editable: boolean;
    extendedProps: { is_unavailable_reason: boolean };
}> => {
    if (!van) return [];
    const events: Array<{
        id: string;
        title: string;
        start: Date;
        end: Date;
        allDay: boolean;
        display: string;
        classNames: string[];
        editable: boolean;
        extendedProps: { is_unavailable_reason: boolean };
    }> = [];

    const offOverrides = (van.day_overrides || [])
        .filter(o => !o.is_working_override)
        .sort((a, b) => a.date.localeCompare(b.date));

    const groups: Array<typeof offOverrides> = [];
    for (const override of offOverrides) {
        const last = groups[groups.length - 1];
        if (last) {
            const prevDate = new Date(`${last[last.length - 1].date}T00:00:00`);
            prevDate.setDate(prevDate.getDate() + 1);
            if (toDateKey(prevDate) === override.date) {
                last.push(override);
                continue;
            }
        }
        groups.push([override]);
    }

    for (const group of groups) {
        const reason = group.map(o => cleanOverrideReason(o.reason)).find(r => r) || '';
        if (!reason) continue;
        const startKey = group[0].date;
        const endKey = group[group.length - 1].date;
        events.push({
            id: `unavail-override-${startKey}`,
            title: reason,
            start: new Date(`${startKey}T00:00:00`),
            end: new Date(`${addDaysToDateKey(endKey, 1)}T00:00:00`),
            allDay: true,
            display: 'block',
            classNames: ['fc-unavailable-reason-event'],
            editable: false,
            extendedProps: { is_unavailable_reason: true },
        });
    }

    for (const block of (van.blocked_dates || [])) {
        const reason = cleanBlockedReason(block.reason);
        if (!reason) continue;
        events.push({
            id: `unavail-blocked-${block.start_date}`,
            title: reason,
            start: new Date(`${block.start_date}T00:00:00`),
            end: new Date(`${addDaysToDateKey(block.end_date, 1)}T00:00:00`),
            allDay: true,
            display: 'block',
            classNames: ['fc-unavailable-reason-event'],
            editable: false,
            extendedProps: { is_unavailable_reason: true },
        });
    }

    return events;
};

const getDayDiff = (start: Date, endExclusive: Date): number => {
    const msPerDay = 1000 * 60 * 60 * 24;
    return Math.max(1, Math.round((endExclusive.getTime() - start.getTime()) / msPerDay));
};

const parseDateKey = (dateKey: string): Date => new Date(`${dateKey}T00:00:00`);

const addDaysToDateKey = (dateKey: string, days: number): string => {
    const date = parseDateKey(dateKey);
    date.setDate(date.getDate() + days);
    return toDateKey(date);
};

const diffDateKeys = (startKey: string, endExclusiveKey: string): number => {
    const start = parseDateKey(startKey);
    const end = parseDateKey(endExclusiveKey);
    return getDayDiff(start, end);
};

const dayOffsetFromKey = (startKey: string, targetKey: string): number => {
    return diffDateKeys(startKey, targetKey) - 1;
};

const getUnavailableRanges = (start: Date, endExclusive: Date, allocatedDates: string[]): Array<{ start: number; length: number }> => {
    const spanDays = getDayDiff(start, endExclusive);
    const allocatedSet = new Set(allocatedDates || []);
    const ranges: Array<{ start: number; length: number }> = [];

    let rangeStart: number | null = null;
    let rangeLength = 0;

    for (let idx = 0; idx < spanDays; idx += 1) {
        const dayDate = new Date(start);
        dayDate.setDate(start.getDate() + idx);
        const isWorkingDay = allocatedSet.has(toDateKey(dayDate));

        if (!isWorkingDay) {
            if (rangeStart === null) {
                rangeStart = idx;
                rangeLength = 1;
            } else {
                rangeLength += 1;
            }
        } else if (rangeStart !== null) {
            ranges.push({ start: rangeStart, length: rangeLength });
            rangeStart = null;
            rangeLength = 0;
        }
    }

    if (rangeStart !== null) {
        ranges.push({ start: rangeStart, length: rangeLength });
    }

    return ranges;
};

const clearGapOverlays = (eventEl: HTMLElement) => {
    const existingOverlays = eventEl.querySelectorAll('.fc-bridged-gap-overlay');
    existingOverlays.forEach((node: Element) => node.remove());
};

const drawGapOverlays = (eventEl: HTMLElement, unavailableDayKeys: string[]) => {
    clearGapOverlays(eventEl);
    if (!unavailableDayKeys.length) return;

    const eventRect = eventEl.getBoundingClientRect();
    if (!eventRect.width || !eventRect.height) return;

    eventEl.style.position = 'relative';
    unavailableDayKeys.forEach((dayKey) => {
        const dayCell = document.querySelector(`.fc-daygrid-day[data-date="${dayKey}"]`) as HTMLElement | null;
        if (!dayCell) return;

        const dayRect = dayCell.getBoundingClientRect();
        const left = Math.max(0, dayRect.left - eventRect.left);
        const right = Math.min(eventRect.width, dayRect.right - eventRect.left);
        const width = Math.max(0, right - left);
        if (width <= 0) return;

        const overlay = document.createElement('div');
        overlay.className = 'fc-bridged-gap-overlay';
        overlay.style.position = 'absolute';
        overlay.style.left = `${left}px`;
        overlay.style.top = '0';
        overlay.style.width = `${width}px`;
        overlay.style.height = '100%';
        overlay.style.pointerEvents = 'none';
        overlay.style.backgroundImage =
            'repeating-linear-gradient(135deg, rgba(220, 38, 38, 0.55) 0px, rgba(220, 38, 38, 0.55) 8px, rgba(220, 38, 38, 0.18) 8px, rgba(220, 38, 38, 0.18) 16px)';
        eventEl.appendChild(overlay);
    });
};

export default function Calendar() {
    // 1. Create a ref for the FullCalendar component
    const calendarRef = useRef<FullCalendar>(null);

    const [openRecurringBookingId, setOpenRecurringBookingId] = useState(0);
    const [openBookingId, setOpenBookingId] = useState(0);
    const [openBookingModal, setOpenBookingModal] = useState(false);
    const [isCalendarLoading, setIsCalendarLoading] = useState(false);
    const [openRecurringBookingModal, setOpenRecurringBookingModal] = useState(false);
    const [openRotaModal, setOpenRotaModal] = useState(false);
    const [isMobile, setIsMobile] = useState(isMobileView());
    const [pendingDrop, setPendingDrop] = useState<PendingDrop | null>(null);
    const [isSavingMove, setIsSavingMove] = useState(false);
    const moveInFlightRef = useRef(false);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);

    useEffect(() => {
        GetUser().then((res) => setIsSuperAdmin((res.data.location_permissions || []).includes('super_admin'))).catch(() => {});
    }, []);

    // Handle responsive view changes
    useEffect(() => {
        const handleResize = () => {
            const mobile = isMobileView();
            setIsMobile(mobile);
            
            // Switch calendar view on resize
            if (calendarRef.current) {
                const calendarApi = calendarRef.current.getApi();
                const currentView = calendarApi.view.type;
                
                if (mobile && (currentView === 'dayGridMonth' || currentView === 'timeGridWeek')) {
                    calendarApi.changeView('listWeek');
                } else if (!mobile && currentView === 'listWeek') {
                    calendarApi.changeView('dayGridMonth');
                }
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const location = localStorage.getItem('location');
    const [bookingsData, setBookingsData] = useState<Booking[]>([]);

    // State for filtering
    const [selectedLocation, setSelectedLocation] = useState('All Locations');
    const [selectedVan, setSelectedVan] = useState('All Vans');
    const [selectedVanId, setSelectedVanId] = useState<number | null>(null);
    const [franchiseVans, setFranchiseVans] = useState<Van[]>([]);
    const hasUserChangedVanFilterRef = useRef(false);
    const selectedVanDetailsRef = useRef<Van | null>(null);
    const overlayRefreshTimersRef = useRef<number[]>([]);

    const refreshBridgedGapOverlays = useCallback(() => {
        const eventNodes = document.querySelectorAll('.fc-event[data-gap-day-keys]');
        eventNodes.forEach((node) => {
            const eventEl = node as HTMLElement;
            const raw = eventEl.dataset.gapDayKeys;
            if (!raw) return;
            try {
                const dayKeys = JSON.parse(raw) as string[];
                drawGapOverlays(eventEl, Array.isArray(dayKeys) ? dayKeys : []);
            } catch (_err) {
                clearGapOverlays(eventEl);
            }
        });
    }, []);

    const scheduleOverlayRefresh = useCallback(() => {
        overlayRefreshTimersRef.current.forEach((id) => window.clearTimeout(id));
        overlayRefreshTimersRef.current = [];

        // Multi-pass refresh to catch FullCalendar's async reflow during resize.
        [0, 120, 320].forEach((delay) => {
            const timerId = window.setTimeout(() => {
                window.requestAnimationFrame(() => {
                    refreshBridgedGapOverlays();
                });
            }, delay);
            overlayRefreshTimersRef.current.push(timerId);
        });
    }, [refreshBridgedGapOverlays]);

    useEffect(() => {
        const onResize = () => {
            scheduleOverlayRefresh();
        };

        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [scheduleOverlayRefresh]);

    useEffect(() => {
        const calendarRoot = calendarRef.current?.getApi()?.el as HTMLElement | undefined;
        if (!calendarRoot || typeof ResizeObserver === 'undefined') {
            return;
        }

        const observer = new ResizeObserver(() => {
            scheduleOverlayRefresh();
        });
        observer.observe(calendarRoot);

        return () => {
            observer.disconnect();
        };
    }, [scheduleOverlayRefresh]);

    useEffect(() => {
        return () => {
            overlayRefreshTimersRef.current.forEach((id) => window.clearTimeout(id));
            overlayRefreshTimersRef.current = [];
        };
    }, []);

    // Derive unique locations
    const uniqueLocations = useMemo(() => {
        const locations = new Set<string>(['All Locations']);
        bookingsData.forEach(booking => {
            const location = booking.customer?.franchise_postcode?.franchise?.location;
            if (location) {
                locations.add(location);
            }
        });
        return Array.from(locations).sort();
    }, [bookingsData]);

    // Derive unique vans
    const uniqueVans = useMemo(() => {
        const vans = new Map<number, string>();
        vans.set(-1, 'All Vans');
        franchiseVans.forEach((van) => vans.set(van.id, van.name));
        bookingsData.forEach(booking => {
            if (booking.van) {
                vans.set(booking.van.id, booking.van.name);
            }
            (booking.returns || []).forEach((bookingReturn) => {
                if (bookingReturn.van) {
                    vans.set(bookingReturn.van.id, bookingReturn.van.name);
                }
            });
        });
        return Array.from(vans.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => {
                if (a.name === 'All Vans') return -1;
                if (b.name === 'All Vans') return 1;
                return a.name.localeCompare(b.name);
            });
    }, [bookingsData, franchiseVans]);

    useEffect(() => {
        // For "All" location mode (multi-location), keep van list from loaded bookings.
        if (location === 'All') {
            setFranchiseVans([]);
            return;
        }

        let mounted = true;
        const loadFranchiseVans = async () => {
            try {
                const franchise = await GeneralApiService.getFranchiseByName();
                const vansResp = await GetFranchiseVans(franchise.id);
                if (!mounted) return;
                const vans = (vansResp?.data || []).map((van: any) => ({
                    id: van.id,
                    name: van.name,
                    weekly_schedule: van.weekly_schedule || {},
                    blocked_dates: van.blocked_dates || [],
                    day_overrides: van.day_overrides || [],
                }));
                setFranchiseVans(vans);
            } catch (err) {
                console.error("Failed to load franchise vans for calendar filter:", err);
                if (mounted) setFranchiseVans([]);
            }
        };

        loadFranchiseVans();
        return () => { mounted = false; };
    }, [location]);

    useEffect(() => {
        const actualVans = uniqueVans.filter((van) => van.id !== -1);
        const selectedVanStillExists = selectedVanId !== null && actualVans.some((van) => van.id === selectedVanId);

        if (selectedVanStillExists) {
            return;
        }

        if (hasUserChangedVanFilterRef.current) {
            setSelectedVan('All Vans');
            setSelectedVanId(null);
            return;
        }

        if (actualVans.length === 1) {
            setSelectedVan(actualVans[0].name);
            setSelectedVanId(actualVans[0].id);
            return;
        }

        // Two or more vans defaults to "All Vans".
        setSelectedVan('All Vans');
        setSelectedVanId(null);
    }, [uniqueVans, selectedVanId]);

    const fetchBookingsForCalendar = useCallback(
        async (fetchInfo, successCallback, failureCallback) => {
            setIsCalendarLoading(true);
            try {
                const { start, end } = fetchInfo;

                const allLoadedBookings: Booking[] = await ApiService.getBookings({
                    page: -1,
                    startRange: start.toISOString(),
                    endRange: end.toISOString()
                });

                setBookingsData(allLoadedBookings);

                // Apply filters
                const locationFilteredBookings = allLoadedBookings.filter((booking) => {
                    if (selectedLocation === 'All Locations') return true;
                    const location = booking.customer?.franchise_postcode?.franchise?.location;
                    return location === selectedLocation;
                });

                const filteredBookings = locationFilteredBookings.filter((booking) => {
                    if (selectedVanId === null) return true;
                    if (!booking.van) return true;
                    return booking.van.id === selectedVanId;
                });

                const bookingEvents = filteredBookings
                    .filter((booking) => booking.booked_in !== null)
                    .map((booking) => {
                        const startDateKey = extractDateKey(String(booking.booked_in));
                        const startDate = new Date(`${startDateKey}T00:00:00`);
                        const daysRequired = Math.max(1, Number(booking.days_to_complete || 1));
                        const allocatedDates = (booking.allocated_dates || []).slice().sort();
                        let endExclusiveDateKey: string;
                        if (allocatedDates.length >= daysRequired) {
                            endExclusiveDateKey = addDaysToDateKey(allocatedDates[allocatedDates.length - 1], 1);
                        } else if (booking.allocated_end_date && allocatedDates.length === 0) {
                            endExclusiveDateKey = addDaysToDateKey(booking.allocated_end_date, 1);
                        } else {
                            endExclusiveDateKey = addDaysToDateKey(startDateKey, daysRequired);
                        }
                        const endDate = new Date(`${endExclusiveDateKey}T00:00:00`);
                        const eventSpanDays = diffDateKeys(startDateKey, endExclusiveDateKey);
                        const hasNonWorkingGap = allocatedDates.length > 0 && allocatedDates.length < eventSpanDays;

                        return {
                            editable: !booking.recurring_booking,
                            id: String(booking.id) || 'Unknown',
                            title: booking.customer?.full_name || 'Unknown Name',
                            start: startDate,
                            end: endDate,
                            allDay: true,
                            extendedProps: {
                                recurring_booking: !!booking.recurring_booking,
                                recurring_booking_id: booking.recurring_booking,
                                services: formatServices(booking.services),
                                postcode: booking.customer?.postcode || '',
                                days_to_complete: booking.days_to_complete || 1,
                                van_name: booking.van?.name || 'Unknown Van',
                                allocated_dates: allocatedDates,
                                has_non_working_gap: hasNonWorkingGap,
                                start_date_key: startDateKey,
                                end_exclusive_date_key: endExclusiveDateKey,
                                job_status: booking.job_status || '',
                                paid: booking.paid || false,
                                return_dates: (booking.returns || [])
                                    .filter((ret) => !!ret.return_date)
                                    .map((ret) => ret.return_date),
                                ai_confirmed: booking.ai_confirmed ?? null,
                                ai_confirmation_note: booking.ai_confirmation_note || '',
                                pre_booking_stories: booking.pre_booking_stories ?? null,
                                referral_badge_label: booking.referral_badge_label || '',
                                is_referral_reward: !!booking.is_referral_reward,
                                is_referred_booking: !!booking.is_referred_booking,
                            },
                        };
                    });

                const returnEvents = locationFilteredBookings.flatMap((booking) => {
                    const customerName = booking.customer?.full_name || 'Unknown Name';
                    const postcode = booking.customer?.postcode || '';
                    const services = formatServices(booking.services || []);
                    const bookingReturns = booking.returns || [];
                    return bookingReturns
                        .filter((bookingReturn) => !!bookingReturn.return_date)
                        .filter((bookingReturn) => selectedVanId === null || bookingReturn.van?.id === selectedVanId)
                        .map((bookingReturn) => {
                            const returnDateKey = bookingReturn.return_date;
                            const returnDaysRequired = Math.max(1, Number(bookingReturn.days_to_complete || 1));
                            const startDate = new Date(`${returnDateKey}T00:00:00`);
                            const endDate = new Date(`${addDaysToDateKey(returnDateKey, returnDaysRequired)}T00:00:00`);
                            return {
                                editable: false,
                                id: `return-${bookingReturn.id}`,
                                title: `${customerName} (Return)`,
                                classNames: ['fc-return-event'],
                                start: startDate,
                                end: endDate,
                                allDay: true,
                                extendedProps: {
                                    is_return: true,
                                    parent_booking_id: booking.id,
                                    reason: bookingReturn.reason || '',
                                    days_to_complete: returnDaysRequired,
                                    job_status: booking.job_status || '',
                                    paid: booking.paid || false,
                                    services,
                                    postcode,
                                    van_name: bookingReturn.van?.name || 'Unknown Van',
                                },
                            };
                        });
                });

                const reasonEvents = buildUnavailableReasonEvents(selectedVanDetailsRef.current);
                successCallback([...bookingEvents, ...returnEvents, ...reasonEvents]);
            } catch (error) {
                console.error("Error fetching bookings:", error);
                failureCallback(error);
            } finally {
                setIsCalendarLoading(false);
            }
        },
        [selectedLocation, selectedVanId]
    );

    const handleEventDrop = async (info: any) => {
        if (info.event.extendedProps?.is_unavailable_reason) {
            info.revert();
            return;
        }
        if (info.event.extendedProps?.is_return) {
            info.revert();
            return;
        }
        if (moveInFlightRef.current) {
            info.revert();
            return;
        }

        if (!info.event.start) {
            info.revert();
            return;
        }

        const newDate = toDateKey(info.event.start);
        const bookingId = Number(info.event.id);
        const bookingTitle = info.event.title || 'this booking';
        const booking = bookingsData.find((item) => item.id === bookingId);
        const vanFromApi = booking?.van?.id
            ? (franchiseVans.find((van) => van.id === booking.van.id) || booking.van)
            : null;
        if (vanFromApi && !isVanWorkingOnDate(vanFromApi, info.event.start)) {
            info.revert();
            window.alert(`Cannot move this booking to ${toDateKey(info.event.start)} because ${vanFromApi.name} is unavailable (blocked/off day).`);
            return;
        }

        setPendingDrop({ info, bookingId, newDate, bookingTitle });
    };

    const handleConfirmMove = async (notifyCustomer: boolean) => {
        if (!pendingDrop || moveInFlightRef.current) return;

        moveInFlightRef.current = true;
        setIsSavingMove(true);

        try {
            await ApiService.updateBookingTime(pendingDrop.bookingId, pendingDrop.newDate, notifyCustomer);
            if (calendarRef.current) {
                calendarRef.current.getApi().refetchEvents();
            }
        } catch (error) {
            console.error(`Failed to update booking time for booking ${pendingDrop.bookingId}:`, error);
            pendingDrop.info.revert();
        } finally {
            moveInFlightRef.current = false;
            setIsSavingMove(false);
            setPendingDrop(null);
        }
    };

    const handleCancelMove = () => {
        if (pendingDrop) {
            pendingDrop.info.revert();
        }
        setPendingDrop(null);
    };

    // 2. Update the handleCallback function to accept updateInfo and refetch events
    const handleCallback = (updateInfo?: { id: number }) => {
        setOpenBookingModal(false);
        setOpenRotaModal(false);
        setOpenRecurringBookingModal(false);

        // If updateInfo exists, it means a save/update occurred in the drawer.
        if (updateInfo) {
            // Access the FullCalendar API via the ref and refetch events
            if (calendarRef.current) {
                const calendarApi = calendarRef.current.getApi();
                calendarApi.refetchEvents();
            }
        }
    };

    const handleEventClick = (info: any) => {
        if (info.event._def.extendedProps?.is_unavailable_reason) return;
        if (info.event._def.extendedProps?.is_return) {
            setOpenBookingId(Number(info.event._def.extendedProps.parent_booking_id));
            setOpenBookingModal(true);
            return;
        }
        const bookingId = info.event.id;
        if (info.event._def.extendedProps.recurring_booking) {
            setOpenRecurringBookingId(info.event._def.extendedProps.recurring_booking_id.id);
            setOpenRecurringBookingModal(true);
        } else {
            setOpenBookingId(Number(bookingId));
            setOpenBookingModal(true);
        }
    };

    const handleRotaModalClick = () => {
        setOpenRotaModal(true);
    };

    const jumpToReturnDate = (dateKey: string, event?: React.MouseEvent) => {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }
        if (!calendarRef.current || !dateKey) return;
        const calendarApi = calendarRef.current.getApi();
        calendarApi.gotoDate(`${dateKey}T00:00:00`);
    };

    const handleLocationChange = (
        _event: React.SyntheticEvent | null,
        newValue: string | null,
    ) => {
        setSelectedLocation(newValue || 'All Locations');
    };

    const handleVanChange = (
        _event: React.SyntheticEvent | null,
        newValue: string | null,
    ) => {
        hasUserChangedVanFilterRef.current = true;
        const nextVanName = newValue || 'All Vans';
        setSelectedVan(nextVanName);
        if (nextVanName === 'All Vans') {
            setSelectedVanId(null);
            return;
        }
        const matchedVan = uniqueVans.find((van) => van.name === nextVanName);
        setSelectedVanId(matchedVan && matchedVan.id !== -1 ? matchedVan.id : null);
    };

    const selectedVanDetails = useMemo(() => {
        if (!selectedVanId) return null;
        const selectedFromFranchiseVans = franchiseVans.find((van) => van.id === selectedVanId);
        if (selectedFromFranchiseVans) return selectedFromFranchiseVans;
        const bookingForSelectedVan = bookingsData.find((booking) => booking.van?.id === selectedVanId);
        return bookingForSelectedVan?.van || null;
    }, [bookingsData, selectedVanId, franchiseVans]);

    useEffect(() => {
        selectedVanDetailsRef.current = selectedVanDetails;
    }, [selectedVanDetails]);

    const isSelectedVanWorkingOnDate = useCallback((date: Date) => {
        return isVanWorkingOnDate(selectedVanDetails, date);
    }, [selectedVanDetails]);

    const getDayCellClassNames = useCallback((arg: any) => {
        if (!selectedVanDetails) return [];
        return isSelectedVanWorkingOnDate(arg.date) ? [] : ['fc-day-unavailable'];
    }, [selectedVanDetails, isSelectedVanWorkingOnDate]);

    const renderEventContent = (eventInfo: any) => {
        if (eventInfo.event.extendedProps?.is_unavailable_reason) {
            return (
                <Box sx={{ px: 0.75, py: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Typography level="body-xs" sx={{ color: '#fff', fontWeight: 600, fontSize: '0.65rem', lineHeight: 1.2 }}>
                        {eventInfo.event.title}
                    </Typography>
                </Box>
            );
        }
        const { services, postcode, van_name, allocated_dates, has_non_working_gap, job_status, paid, is_return, reason, days_to_complete, return_dates, ai_confirmed, ai_confirmation_note, pre_booking_stories, referral_badge_label, is_referral_reward } = eventInfo.event.extendedProps;
        if (is_return) {
            const isComplete = job_status === 'Mark As Complete';
            const isCancelled = job_status === 'Mark As Cancelled';
            const needsRescheduling = job_status === 'Mark As Needs Rescheduling';
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, padding: '6px 8px', height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', justifyContent: 'space-between' }}>
                        <Chip
                            size="sm"
                            variant="solid"
                            color="warning"
                            startDecorator={<LocalShippingRoundedIcon sx={{ fontSize: '0.75rem' }} />}
                            sx={{
                                fontSize: '0.65rem',
                                minHeight: '20px',
                                height: '20px',
                                fontWeight: 600,
                                background: 'white',
                                color: 'black'
                            }}
                        >
                            {van_name}
                        </Chip>
                        <Box sx={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
                            <Chip
                                size="sm"
                                variant="solid"
                                color="warning"
                                sx={{ fontSize: '0.6rem', minHeight: '18px', height: '18px', fontWeight: 700, px: 0.75 }}
                            >
                                Return
                            </Chip>
                            <Chip
                                size="sm"
                                variant="solid"
                                sx={{
                                    fontSize: '0.6rem',
                                    minHeight: '18px',
                                    height: '18px',
                                    fontWeight: 700,
                                    px: 0.75,
                                    background: is_referral_reward ? '#7c3aed' : paid ? '#16a34a' : '#dc2626',
                                    color: 'white',
                                }}
                            >
                                {is_referral_reward ? 'Free' : paid ? 'Paid' : 'Not Paid'}
                            </Chip>
                            {referral_badge_label && (
                                <Chip
                                    size="sm"
                                    variant="solid"
                                    sx={{
                                        fontSize: '0.6rem',
                                        minHeight: '18px',
                                        height: '18px',
                                        fontWeight: 700,
                                        px: 0.75,
                                        background: '#7c3aed',
                                        color: 'white',
                                    }}
                                >
                                    {referral_badge_label}
                                </Chip>
                            )}
                            <Chip
                                size="sm"
                                variant="solid"
                                sx={{
                                    fontSize: '0.6rem',
                                    minHeight: '18px',
                                    height: '18px',
                                    fontWeight: 700,
                                    px: 0.75,
                                    background: isComplete ? '#16a34a' :
                                        isCancelled ? '#dc2626' :
                                        needsRescheduling ? '#d97706' : 'rgba(255,255,255,0.25)',
                                    color: 'white',
                                }}
                            >
                                {isComplete ? 'Complete' :
                                    isCancelled ? 'Cancelled' :
                                        needsRescheduling ? 'Reschedule' : 'Pending'}
                            </Chip>
                        </Box>
                    </Box>
                    <Typography level="body-xs" sx={{ color: 'white', fontWeight: 700, fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
                        {eventInfo.event.title}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>
                        Reason: {reason || 'Return visit'}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'rgba(255, 255, 255, 0.95)', fontSize: '0.68rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textShadow: '0 1px 2px rgba(0,0,0,0.15)' }}>
                        Duration: {Math.max(1, Number(days_to_complete || 1))} day{Math.max(1, Number(days_to_complete || 1)) === 1 ? '' : 's'}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.7rem', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textShadow: '0 1px 2px rgba(0,0,0,0.15)' }}>
                        📍 {postcode}
                    </Typography>
                </Box>
            );
        }
        const workingDaysLabel = Array.isArray(allocated_dates) && allocated_dates.length
            ? allocated_dates.map((d: string) => {
                const dayDate = new Date(`${d}T00:00:00`);
                return dayDate.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' });
            }).join(', ')
            : '';

        const isComplete = job_status === 'Mark As Complete';
        const isCancelled = job_status === 'Mark As Cancelled';
        const needsRescheduling = job_status === 'Mark As Needs Rescheduling';
        const showChips = eventInfo.isEnd;

        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                padding: '6px 8px',
                height: '100%'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', justifyContent: 'space-between' }}>
                    <Chip
                        size="sm"
                        variant="solid"
                        color="success"
                        startDecorator={<LocalShippingRoundedIcon sx={{ fontSize: '0.75rem' }} />}
                        sx={{
                            fontSize: '0.65rem',
                            minHeight: '20px',
                            height: '20px',
                            fontWeight: 600,
                            background: 'white',
                            color: 'black'
                        }}
                    >
                        {van_name}
                    </Chip>
                    {showChips && (
                        <Box sx={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
                            {typeof pre_booking_stories === 'number' && pre_booking_stories >= 3 && (
                                <Tooltip title={`Cherry picker needed (${pre_booking_stories} storeys)`} placement="top" arrow>
                                    <Chip
                                        size="sm"
                                        variant="solid"
                                        startDecorator={<ConstructionRoundedIcon sx={{ fontSize: '0.85rem', color: 'white' }} />}
                                        sx={{
                                            fontSize: '0.6rem',
                                            minHeight: '18px',
                                            height: '18px',
                                            fontWeight: 700,
                                            px: 0.75,
                                            background: '#f97316',
                                            color: 'white',
                                            cursor: 'help',
                                        }}
                                    >
                                        Cherry Picker
                                    </Chip>
                                </Tooltip>
                            )}
                            <Chip
                                size="sm"
                                variant="solid"
                                sx={{
                                    fontSize: '0.6rem',
                                    minHeight: '18px',
                                    height: '18px',
                                    fontWeight: 700,
                                    px: 0.75,
                                    background: paid ? '#16a34a' : '#dc2626',
                                    color: 'white',
                                }}
                            >
                                {paid ? 'Paid' : 'Not Paid'}
                            </Chip>
                            <Chip
                                size="sm"
                                variant="solid"
                                sx={{
                                    fontSize: '0.6rem',
                                    minHeight: '18px',
                                    height: '18px',
                                    fontWeight: 700,
                                    px: 0.75,
                                    background: isComplete ? '#16a34a' :
                                        isCancelled ? '#dc2626' :
                                        needsRescheduling ? '#d97706' : 'rgba(255,255,255,0.25)',
                                    color: 'white',
                                }}
                            >
                                {isComplete ? 'Complete' :
                                 isCancelled ? 'Cancelled' :
                                 needsRescheduling ? 'Reschedule' : 'Pending'}
                            </Chip>
                            {isSuperAdmin && ai_confirmed === false && (
                                <Tooltip title={ai_confirmation_note || 'Customer has not confirmed this booking'} placement="top" arrow>
                                    <Chip
                                        size="sm"
                                        variant="solid"
                                        startDecorator={<SmartToyRounded sx={{ fontSize: '0.85rem', color: 'primary.plainColor' }} />}
                                        sx={{
                                            fontSize: '0.65rem',
                                            minHeight: '20px',
                                            height: '20px',
                                            fontWeight: 700,
                                            px: 0.75,
                                            background: '#f59e0b',
                                            color: 'white',
                                            cursor: 'help',
                                        }}
                                    >
                                        Unconfirmed
                                    </Chip>
                                </Tooltip>
                            )}
                            {isSuperAdmin && ai_confirmed === null && ai_confirmed !== undefined && (
                                <Chip
                                    size="sm"
                                    variant="solid"
                                    startDecorator={<SmartToyRounded sx={{ fontSize: '0.85rem', color: 'primary.plainColor' }} />}
                                    sx={{
                                        fontSize: '0.65rem',
                                        minHeight: '20px',
                                        height: '20px',
                                        fontWeight: 700,
                                        px: 0.75,
                                        background: 'rgba(255,255,255,0.35)',
                                        color: 'white',
                                    }}
                                >
                                    {ai_confirmation_note ? 'Analysing...' : 'No Analysis'}
                                </Chip>
                            )}
                        </Box>
                    )}
                </Box>
                <Typography
                    level="body-xs"
                    sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                    }}
                >
                    {eventInfo.event.title}
                </Typography>
                <Typography
                    level="body-xs"
                    sx={{
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        whiteSpace: 'wrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                    }}
                >
                    ● {services}
                </Typography>
                <Typography
                    level="body-xs"
                    sx={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        textShadow: '0 1px 2px rgba(0,0,0,0.15)'
                    }}
                >
                    📍 {postcode}
                </Typography>
                {has_non_working_gap && (
                    <Typography
                        level="body-xs"
                        sx={{
                            color: '#fff7ed',
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            textShadow: '0 1px 2px rgba(0,0,0,0.25)'
                        }}
                    >
                        Bridged job: working on {workingDaysLabel}
                    </Typography>
                )}
                {Array.isArray(return_dates) && return_dates.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        <Typography
                            level="body-xs"
                            sx={{
                                color: '#fff7ed',
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                mr: 0.5,
                            }}
                        >
                            Returns:
                        </Typography>
                        {return_dates.slice(0, 3).map((dateKey: string) => (
                            <Chip
                                key={dateKey}
                                size="sm"
                                variant="soft"
                                color="warning"
                                onClick={(event) => jumpToReturnDate(dateKey, event)}
                                sx={{
                                    fontSize: '0.58rem',
                                    minHeight: '16px',
                                    cursor: 'pointer',
                                }}
                            >
                                {new Date(`${dateKey}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' })}
                            </Chip>
                        ))}
                        {return_dates.length > 3 && (
                            <Typography level="body-xs" sx={{ color: '#fff7ed', fontSize: '0.62rem' }}>
                                +{return_dates.length - 3} more
                            </Typography>
                        )}
                    </Box>
                )}

            </Box>
        );
    };


    return (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            height: '100%',
            minHeight: { xs: 'calc(100vh - var(--Header-height) - 32px)', sm: 'auto' },
            overflowY: { xs: 'auto', sm: 'visible' },
        }}>
            <RotaModal parentCallback={handleCallback} openState={openRotaModal} />
            <BookingsDrawer
                key={'bookings-modal-' + openBookingModal}
                openState={openBookingModal}
                newBooking={null}
                newBookingValue={null}
                bookingId={openBookingId}
                parentCallback={handleCallback}
            />
            <RecurringBookingsModal
                key={'recurring-bookings-modal-' + openRecurringBookingModal}
                openState={openRecurringBookingModal}
                recurringBookingId={openRecurringBookingId}
                parentCallback={handleCallback}
            />

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Breadcrumbs
                    size="sm"
                    aria-label="breadcrumbs"
                    separator={<ChevronRightRoundedIcon />}
                    sx={{ pl: 0 }}
                >
                    <Link underline="none" color="neutral" href="/dashboard" aria-label="Home">
                        <HomeRoundedIcon />
                    </Link>
                    <Link underline="hover" color="neutral" href="/dashboard" fontSize={12} fontWeight={500}>
                        Dashboard
                    </Link>
                    <Typography color="primary" fontWeight={500} fontSize={12}>
                        Booking Calendar
                    </Typography>
                </Breadcrumbs>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    mb: 1,
                    gap: 1,
                    flexDirection: 'column',
                }}
            >
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                }}>
                    <Typography level="h2" component="h1" sx={{ fontSize: { xs: 'xl', sm: 'xl2' } }}>
                        Booking Calendar
                    </Typography>

                    <Button 
                        onClick={handleRotaModalClick}
                        size={isMobile ? 'sm' : 'md'}
                    >
                        Send Rota
                    </Button>
                </Box>

                {/* Filters row - stacks on mobile */}
                <Box sx={{ 
                    display: 'flex', 
                    gap: { xs: 1, sm: 2 }, 
                    alignItems: 'center', 
                    flexWrap: 'wrap',
                    flexDirection: { xs: 'column', sm: 'row' },
                    width: { xs: '100%', sm: 'auto' },
                }}>
                    {location == 'All' && (
                        <Select
                            placeholder="Filter by location"
                            value={selectedLocation}
                            onChange={handleLocationChange}
                            size="sm"
                            sx={{ minWidth: { xs: '100%', sm: 180 } }}
                        >
                            {uniqueLocations.map((location) => (
                                <Option key={location} value={location}>
                                    {location}
                                </Option>
                            ))}
                        </Select>
                    )}

                    <Select
                        placeholder="Filter by van"
                        value={selectedVan}
                        onChange={handleVanChange}
                        size="sm"
                        sx={{ minWidth: { xs: '100%', sm: 180 } }}
                        startDecorator={<LocalShippingRoundedIcon />}
                    >
                        {uniqueVans.map((van) => (
                            <Option key={van.id} value={van.name}>
                                {van.name}
                            </Option>
                        ))}
                    </Select>
                </Box>
            </Box>

            <Sheet sx={{ 
                padding: { xs: '8px', sm: '25px' }, 
                flexGrow: 1, 
                minHeight: { xs: '500px', sm: '600px' },
                height: { xs: 'calc(100vh - var(--Header-height) - 160px)', sm: 'calc(100vh - var(--Header-height) - 180px)' },
                position: 'relative',
                overflow: 'hidden',
                // Mobile calendar styling
                '& .fc-toolbar': {
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    gap: { xs: 1, sm: 0 },
                },
                '& .fc-toolbar-title': {
                    fontSize: { xs: '1rem', sm: '1.5rem' },
                },
                '& .fc-button': {
                    padding: { xs: '4px 8px', sm: '6px 12px' },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                },
            }}>
                {isCalendarLoading && (
                    <LinearProgress
                        sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10 }}
                    />
                )}
                <FullCalendar
                    ref={calendarRef}
                    height={'100%'}
                    stickyHeaderDates={true}
                    plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
                    initialView={isMobile ? 'listWeek' : 'dayGridMonth'}
                    droppable={true}
                    editable={!isMobile}
                    eventDurationEditable={false}
                    selectMirror={true}
                    events={fetchBookingsForCalendar}
                    eventDrop={handleEventDrop}
                    eventClick={handleEventClick}
                    defaultTimedEventDuration={'01:00'}
                    eventTimeFormat={{
                        hour: 'numeric',
                        minute: '2-digit',
                        meridiem: 'short'
                    }}
                    nextDayThreshold={"09:00:00"}
                    headerToolbar={isMobile ? {
                        left: 'prev,next',
                        center: 'title',
                        right: 'listWeek,timeGridDay'
                    } : {
                        left: 'prev,next today',
                        center: 'title',
                        right: 'dayGridMonth,timeGridWeek,timeGridDay'
                    }}
                    eventContent={renderEventContent}
                    eventDidMount={(arg: any) => {
                        if (arg.event.extendedProps?.is_unavailable_reason) {
                            arg.el.style.pointerEvents = 'none';
                            return;
                        }
                        if (arg.event.extendedProps?.is_return) {
                            return;
                        }
                        const allocatedDates = Array.isArray(arg.event.extendedProps?.allocated_dates)
                            ? arg.event.extendedProps.allocated_dates
                            : [];
                        const startDateKey = arg.event.extendedProps?.start_date_key;
                        const endExclusiveDateKey = arg.event.extendedProps?.end_exclusive_date_key;
                        if (!arg.event.extendedProps?.has_non_working_gap || !arg.event.start || !arg.event.end) {
                            return;
                        }
                        if (!startDateKey || !endExclusiveDateKey) {
                            return;
                        }

                        const spanDays = diffDateKeys(startDateKey, endExclusiveDateKey);
                        if (spanDays <= 1) {
                            delete arg.el.dataset.gapDayKeys;
                            clearGapOverlays(arg.el as HTMLElement);
                            return;
                        }

                        const allocatedSet = new Set(allocatedDates || []);
                        const unavailableDayKeys: string[] = [];
                        for (let i = 0; i < spanDays; i += 1) {
                            const dayKey = addDaysToDateKey(startDateKey, i);
                            if (!allocatedSet.has(dayKey)) {
                                unavailableDayKeys.push(dayKey);
                            }
                        }
                        if (!unavailableDayKeys.length) return;
                        arg.el.dataset.gapDayKeys = JSON.stringify(unavailableDayKeys);
                        drawGapOverlays(arg.el as HTMLElement, unavailableDayKeys);
                        scheduleOverlayRefresh();
                    }}
                    eventWillUnmount={(arg: any) => {
                        delete arg.el.dataset.gapDayKeys;
                        clearGapOverlays(arg.el as HTMLElement);
                    }}
                    datesSet={() => {
                        scheduleOverlayRefresh();
                    }}
                    dayCellClassNames={getDayCellClassNames}
                    loading={setIsCalendarLoading}
                />
            </Sheet>
            <ConfirmMoveBookingModal
                open={!!pendingDrop}
                bookingTitle={pendingDrop?.bookingTitle || 'this booking'}
                newDate={pendingDrop?.newDate ? new Date(pendingDrop.newDate).toLocaleDateString('en-GB') : ''}
                onConfirm={handleConfirmMove}
                onCancel={handleCancelMove}
                isSubmitting={isSavingMove}
            />
        </Box>
    );
}